import type { MedplumClient } from '@medplum/core'

const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/gif': 'gif',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
}

const DOWNLOAD_OBJECT_URL_REVOKE_DELAY_MS = 1000
const KNOWN_FILE_EXTENSIONS = new Set(Object.values(MIME_TYPE_EXTENSIONS))

type DownloadAttachmentFileOptions = {
  fallbackExtension?: string
}

function isBinary(url: string): boolean {
  return url.startsWith('Binary')
}

function isDataUrl(url: string): boolean {
  return url.startsWith('data:')
}

export async function downloadAttachment(medplum: MedplumClient, url: string): Promise<Blob> {
  if (isBinary(url)) {
    return medplum.download(url)
  }

  if (isDataUrl(url)) {
    const response = await fetch(url)
    return response.blob()
  }

  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'follow',
  })

  if (!response.ok) {
    throw new Error(`Failed to download attachment: ${response.status} ${response.statusText}`)
  }

  return response.blob()
}

function getExtensionFromContentType(contentType: string): string | undefined {
  return MIME_TYPE_EXTENSIONS[contentType.split(';')[0]?.toLowerCase()]
}

function getExtensionFromUrl(url: string): string | undefined {
  const dataUrlContentType = /^data:([^;,]+)/i.exec(url)?.[1]
  const dataUrlExtension = dataUrlContentType
    ? getExtensionFromContentType(dataUrlContentType)
    : undefined

  if (dataUrlExtension) {
    return dataUrlExtension
  }

  const sourcePath = url.split(/[?#]/, 1)[0] ?? url
  const urlExtension = /\.([a-z0-9]{2,5})$/i.exec(sourcePath)?.[1]?.toLowerCase()

  return urlExtension && KNOWN_FILE_EXTENSIONS.has(urlExtension) ? urlExtension : undefined
}

function normalizeExtension(extension?: string): string | undefined {
  const normalizedExtension = extension?.replace(/^\./, '').toLowerCase()
  return normalizedExtension && /^[a-z0-9]{2,5}$/.test(normalizedExtension)
    ? normalizedExtension
    : undefined
}

function withFileExtension(
  fileName: string,
  contentType: string,
  sourceUrl: string,
  fallbackExtension?: string,
): string {
  if (/\.[a-z0-9]{2,5}$/i.test(fileName)) {
    return fileName
  }

  const extension =
    getExtensionFromContentType(contentType) ||
    getExtensionFromUrl(sourceUrl) ||
    normalizeExtension(fallbackExtension)

  return extension ? `${fileName}.${extension}` : fileName
}

export async function downloadAttachmentFile(
  medplum: MedplumClient,
  url: string,
  fileName: string,
  options: DownloadAttachmentFileOptions = {},
): Promise<void> {
  const blob = await downloadAttachment(medplum, url)
  saveBlob(blob, withFileExtension(fileName, blob.type, url, options.fallbackExtension))
}

/** Saves a blob the browser already holds as a file download. */
export function saveBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = objectUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()

  // Some browsers start reading the object URL lazily after the click event.
  window.setTimeout(() => {
    URL.revokeObjectURL(objectUrl)
  }, DOWNLOAD_OBJECT_URL_REVOKE_DELAY_MS)
}
